/** @jsx jsx */
import { Button, Grid, IconButton } from "@cd2/components/build/es6"
import { Fields, Form2 } from "@cd2/forms/build/es6"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { P } from "@cd2/text/build/es6"
import { css, jsx } from "@emotion/core"
import { useRef } from "react"
import { icons } from "../../constants/icons"
import { spliceArray } from "../../utils/arrays"
import { defaultDeliveryMethodOptions } from "./DeliveryMethodSelectList"

export function MultiTextField({ field }: { field: string }) {
  const formRef = useRef<FormState | null>(null)

  const formField = Form2.useField<
    {
      value: string
      key: string
      defaultIntercoPrice: number
      defaultNonIntercoPrice?: number
      deliveryType: string | null
    }[]
  >(field, {
    subscribe: true,
    defaultValue: [],
  })

  Form2.useFormSubmitLock(() => {
    const options = formField.getValue()
    const hasDuplicates = options.some(
      option => options.filter(opt => opt.value === option.value).length > 1,
    )
    if (hasDuplicates) {
      formField.setError("Options must be unique. Change or remove duplicate rows.")
      return false
    }
    const hasEmptyOptions = options.some(option => !option.value?.trim())
    if (hasEmptyOptions) {
      formField.setError("Options must contain a value. Change or remove the empty rows.")
      return false
    }
    formField.setError(undefined)
  })

  // Add new item
  function handleAdd(value: string) {
    formField.setValue([
      ...formField.getValue(),
      {
        value,
        key: Math.random().toString(),
        defaultIntercoPrice: 0,
        defaultNonIntercoPrice: undefined,
        deliveryType: null,
      },
    ])
  }

  // Remove item
  function handleRemove(key: string) {
    formField.setValue(formField.getValue().filter(item => item.key !== key))
  }

  // Change item value
  function handleChange(key: string, value: string) {
    const index = formField.getValue().findIndex(item => item.key === key)
    const data = formField.getValue()[index]
    formField.setValue(
      spliceArray(formField.getValue(), index, 1, {
        ...data,
        value,
      }),
    )
  }

  // Change item default interco price
  function handlePriceChange(key: string, defaultIntercoPrice: number) {
    const index = formField.getValue().findIndex(item => item.key === key)
    const currentValue = formField.getValue()[index]
    formField.setValue(
      spliceArray(formField.getValue(), index, 1, {
        ...currentValue,
        defaultIntercoPrice,
      }),
    )
  }
  function handleNonIntercoPriceChange(key: string, price: number) {
    const index = formField.getValue().findIndex(item => item.key === key)
    const currentValue = formField.getValue()[index]
    formField.setValue(
      spliceArray(formField.getValue(), index, 1, {
        ...currentValue,
        defaultNonIntercoPrice: price,
      }),
    )
  }

  function handleFormSubmit(value: string) {
    const field = formRef.current?.getField("newValue")
    if (!value || !value.trim()) {
      field?.setError("must enter a value")
      return
    }
    const hasValue = formField
      .getValue()
      .find(option => option.value.toLowerCase() === value.toLowerCase())

    if (hasValue) {
      field?.setError("option already exists")
      return
    }
    field?.setError(undefined)
    handleAdd(value.trim())
    if (field) {
      field.setValue("")
    }
  }

  function handleChangeDeliveryType(key: string, deliveryType: string) {
    const index = formField.getValue().findIndex(item => item.key === key)
    const currentValue = formField.getValue()[index]
    formField.setValue(
      spliceArray(formField.getValue(), index, 1, {
        ...currentValue,
        deliveryType,
      }),
    )
  }

  const deliveryTypeOptions = ["Direct Delivery", "Courier", "Van", "Collection"].map(x => ({
    label: x,
    value: x,
  }))

  return (
    <Grid
      gap={12}
      css={css`
        width: 100%;
      `}
    >
      <Grid columns={["auto", "max-content"]} alignItems="center">
        <h4>Delivery Options</h4>
        <a
          css={css`
            cursor: pointer;
          `}
          onClick={() => {
            formField.setValue(defaultDeliveryMethodOptions)
          }}
        >
          Reset to defaults
        </a>
      </Grid>
      {formField.getError() && <P color="error">{formField.getError()}</P>}
      {formField.getValue().map(option => {
        const hasDuplicates =
          formField.getValue().filter(opt => opt.value.toLowerCase() === option.value.toLowerCase())
            .length > 1
        const optionIsInvalid = hasDuplicates || !option.value
        return (
          <Grid
            key={option.key}
            columns={[{ fr: 1 }, { fr: 1 }, { fr: 1 }, { fr: 1 }, "max-content"]}
            gap={8}
            css={
              optionIsInvalid
                ? css`
                    input {
                      border-color: red !important;
                    }
                  `
                : undefined
            }
          >
            <Fields.Text
              placeholder="Option"
              value={option.value}
              onChange={value => handleChange(option.key, value)}
              error={
                !option.value ? "must have a value" : hasDuplicates ? "must be unique" : undefined
              }
            />
            <Fields.Price
              placeholder="Default Interco Price"
              value={option.defaultIntercoPrice}
              onChange={value => handlePriceChange(option.key, value ?? 0)}
              error={
                !option.value ? "must have a value" : hasDuplicates ? "must be unique" : undefined
              }
              inPennies
            />
            <Fields.Price
              placeholder="Default Non-Interco Price"
              value={option.defaultNonIntercoPrice ?? null}
              onChange={value => handleNonIntercoPriceChange(option.key, value ?? 0)}
              error={
                !option.value ? "must have a value" : hasDuplicates ? "must be unique" : undefined
              }
              inPennies
            />
            <Fields.Select2
              options={deliveryTypeOptions}
              placeholder="Delivery Type"
              onChange={v => handleChangeDeliveryType(option.key, v?.value ?? null)}
              value={deliveryTypeOptions.find(x => x.value === option.deliveryType) ?? null}
              isClearable
            />
            <IconButton
              buttonStyle="text"
              icon={icons.cross}
              buttonColor="white"
              onClick={() => handleRemove(option.key)}
              square
            />
          </Grid>
        )
      })}
      <Form2.Form xRef={formRef} onSubmit={({ newValue }) => handleFormSubmit(newValue)}>
        <Grid columns={["auto", "max-content"]} gap={8}>
          <Form2.Text
            field="newValue"
            label="Add a new delivery option"
            inputProps={{
              onKeyPress: event => {
                if (event.key === "Enter" && !event.ctrlKey) {
                  event.preventDefault()
                  event.stopPropagation()
                  formRef.current?.submit()
                }
              },
            }}
          />
          <div
            css={css`
              height: 37px;
              margin-top: 14px;
            `}
          >
            <Button
              onClick={() => formRef.current?.submit()}
              buttonStyle="outline"
              buttonColor="#333"
            >
              + Add
            </Button>
          </div>
        </Grid>
      </Form2.Form>
    </Grid>
  )
}
